import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { usePageHeaderSetter } from '@/components/layout/PageHeaderContext';

import {
  type ImportStrategy,
  type SourcePatient,
  useImportPatient,
  useSourceOrganizations,
  useSourcePatients,
  useV2ImportAccess,
} from '../api/v2-import-api';

type RowState = { state: 'idle' | 'importing' | 'done' | 'failed'; detail?: string };

/**
 * Moving patients out of ConsultNow V2, one at a time.
 *
 * The deliberate shape here is that importing is per patient, not per
 * selection: import one, open the chart, satisfy yourself it is right, then
 * come back and do the rest. Importing the same patient twice is safe, so a
 * half-finished run is fixed by running it again.
 */
export function V2ImportPage() {
  const setPageHeader = usePageHeaderSetter();
  const access = useV2ImportAccess();

  const [sourceOrgId, setSourceOrgId] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [rowState, setRowState] = useState<Record<string, RowState>>({});
  const [running, setRunning] = useState(false);

  const canImport = access.data?.can_import ?? false;
  const organizations = useSourceOrganizations(canImport);
  const patients = useSourcePatients(canImport ? sourceOrgId : null);
  const importPatient = useImportPatient();

  useEffect(() => {
    setPageHeader({ title: 'Import from ConsultNow' });
  }, [setPageHeader]);

  useEffect(() => {
    if (!sourceOrgId && organizations.data?.length) {
      setSourceOrgId(organizations.data[0].id);
    }
  }, [organizations.data, sourceOrgId]);

  const rows = patients.data ?? [];
  const importable = useMemo(() => rows.filter((p) => p.status !== 'imported'), [rows]);

  if (access.isLoading) {
    return <p className="text-muted-foreground p-6 text-sm">Loading…</p>;
  }

  if (!access.data?.configured) {
    return (
      <p className="text-muted-foreground p-6 text-sm">
        ConsultNow import is not configured on this installation.
      </p>
    );
  }

  if (!canImport) {
    // The destination is fixed by configuration, so this is the only honest
    // thing to say: it is not your organization's importer.
    return (
      <p className="text-muted-foreground p-6 text-sm">
        Importing is restricted to administrators of {access.data.target_organization_name ?? 'the destination organization'}.
      </p>
    );
  }

  if (organizations.isError) {
    return (
      <p className="text-muted-foreground p-6 text-sm">
        ConsultNow could not be reached. Check that its database is available.
      </p>
    );
  }

  const toggle = (id: string) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const runImport = async (targets: SourcePatient[], strategy: ImportStrategy = 'auto') => {
    if (!sourceOrgId) return;
    setRunning(true);
    let imported = 0;
    // Sequential on purpose: each import is a transaction plus a set of S3
    // copies, and a readable per-row progress beats a faster storm.
    for (const patient of targets) {
      setRowState((s) => ({ ...s, [patient.id]: { state: 'importing' } }));
      try {
        const result = await importPatient.mutateAsync({ sourceOrgId, patientId: patient.id, strategy });
        imported += 1;
        const rowCount = Object.values(result.rows).reduce((total, count) => total + count, 0);
        setRowState((s) => ({
          ...s,
          [patient.id]: {
            state: 'done',
            detail: `${rowCount} records, ${result.objects_copied} files`,
          },
        }));
      } catch (error: unknown) {
        const message =
          (error as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Import failed';
        setRowState((s) => ({ ...s, [patient.id]: { state: 'failed', detail: message } }));
      }
    }
    setRunning(false);
    setSelected(new Set());
    await patients.refetch();
    toast.success(`Imported ${imported} of ${targets.length} patients`);
  };

  return (
    <div className="space-y-6 p-6">
      <Card>
        <CardContent className="flex flex-wrap items-center gap-4 pt-6">
          <div className="min-w-64">
            <Select value={sourceOrgId ?? undefined} onValueChange={setSourceOrgId} disabled={running}>
              <SelectTrigger>
                <SelectValue placeholder="Choose a ConsultNow organization" />
              </SelectTrigger>
              <SelectContent>
                {(organizations.data ?? []).map((org) => (
                  <SelectItem key={org.id} value={org.id}>
                    {org.name} ({org.patient_count} patients)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <p className="text-muted-foreground text-sm">
            Importing into <span className="font-medium">{access.data.target_organization_name}</span>
          </p>
          <div className="ml-auto flex gap-2">
            <Button
              variant="outline"
              disabled={running || importable.length === 0}
              onClick={() => setSelected(new Set(importable.map((p) => p.id)))}
            >
              Select all
            </Button>
            <Button
              disabled={running || selected.size === 0}
              onClick={() => runImport(rows.filter((p) => selected.has(p.id)))}
            >
              {running && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Import {selected.size > 0 ? `${selected.size} ` : ''}selected
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10" />
                <TableHead>Patient</TableHead>
                <TableHead>MRN</TableHead>
                <TableHead className="text-right">Documents</TableHead>
                <TableHead className="text-right">Encounters</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {patients.isLoading && (
                <TableRow>
                  <TableCell colSpan={6} className="text-muted-foreground py-8 text-center text-sm">
                    Loading patients from ConsultNow…
                  </TableCell>
                </TableRow>
              )}
              {!patients.isLoading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-muted-foreground py-8 text-center text-sm">
                    No patients in this organization.
                  </TableCell>
                </TableRow>
              )}
              {rows.map((patient) => {
                const state = rowState[patient.id];
                return (
                  <TableRow key={patient.id}>
                    <TableCell>
                      <Checkbox
                        checked={selected.has(patient.id)}
                        disabled={running || patient.status === 'imported'}
                        onCheckedChange={() => toggle(patient.id)}
                        aria-label={`Select ${patient.display_name}`}
                      />
                    </TableCell>
                    <TableCell className="font-medium">{patient.display_name}</TableCell>
                    <TableCell>{patient.medical_record_number}</TableCell>
                    <TableCell className="text-right">{patient.document_count}</TableCell>
                    <TableCell className="text-right">{patient.encounter_count}</TableCell>
                    <TableCell>
                      <StatusCell patient={patient} state={state} onResolve={(s) => runImport([patient], s)} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function StatusCell({
  patient,
  state,
  onResolve,
}: {
  patient: SourcePatient;
  state?: RowState;
  onResolve: (strategy: ImportStrategy) => void;
}) {
  if (state?.state === 'importing') {
    return (
      <span className="text-muted-foreground flex items-center gap-2 text-sm">
        <Loader2 className="h-4 w-4 animate-spin" /> Importing…
      </span>
    );
  }
  if (state?.state === 'done') {
    return (
      <span className="flex items-center gap-2 text-sm text-emerald-700">
        <CheckCircle2 className="h-4 w-4" /> {state.detail}
      </span>
    );
  }
  if (state?.state === 'failed') {
    return (
      <span className="text-destructive flex items-center gap-2 text-sm">
        <AlertTriangle className="h-4 w-4" /> {state.detail}
      </span>
    );
  }
  if (patient.status === 'imported') {
    return <Badge variant="secondary">Already imported</Badge>;
  }
  if (patient.status === 'conflict') {
    // The MRN belongs to a different chart here. Two people can share an MRN
    // across systems, so this is the operator's call, not a default.
    return (
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="destructive">MRN in use</Badge>
        <Button size="sm" variant="outline" onClick={() => onResolve('merge')}>
          Merge into existing chart
        </Button>
        <Button size="sm" variant="ghost" onClick={() => onResolve('separate')}>
          Keep separate
        </Button>
      </div>
    );
  }
  return <Badge variant="outline">Not imported</Badge>;
}
